/**
 * Chat Bubble - Individual message in the admin chat panel.
 *
 * Picker messages: left-aligned with neon cyan accent.
 * Manager/own messages: right-aligned with surface background.
 * System messages: centered with muted text.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useMissionTheme } from '../theme';
import type { ChatMessage } from '../../../hooks/useOrderChat';

interface ChatBubbleProps {
  message: ChatMessage;
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function ChatBubble({ message }: ChatBubbleProps) {
  const { mc } = useMissionTheme();
  const isOwn = message.isOwn;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start', mb: 1.5 }}>
      {!isOwn && (
        <Typography variant="caption" sx={{ color: mc.secondary, ml: 1, mb: 0.25, fontSize: '0.7rem' }}>
          {message.senderName}
        </Typography>
      )}
      <Box
        sx={{
          maxWidth: '75%',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          ...(isOwn
            ? {
                bgcolor: mc.elevated,
                borderBottomRightRadius: 4,
                border: `1px solid ${mc.glassBorder}`,
              }
            : {
                bgcolor: 'rgba(0, 212, 255, 0.08)',
                borderBottomLeftRadius: 4,
                border: `1px solid rgba(0, 212, 255, 0.2)`,
              }),
        }}
      >
        {message.photoUrl && (
          <Box
            component="img"
            src={message.photoUrl}
            sx={{ width: 200, height: 200, objectFit: 'cover', borderRadius: 1, mb: message.text ? 0.5 : 0 }}
          />
        )}
        {message.text && (
          <Typography variant="body2" sx={{ color: mc.textPrimary, fontSize: '0.85rem', wordBreak: 'break-word' }}>
            {message.text}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" sx={{ color: mc.textMuted, mx: 1, mt: 0.25, fontSize: '0.65rem' }}>
        {formatTime(message.timestamp)}
      </Typography>
    </Box>
  );
}
